import axios from "axios";
import mixpanel from "mixpanel-browser";

import { getTempScribenoteFlask } from "../utils";
import { API_HEADERS, bytesToMB } from "./utils";

export async function signS3Attachment(
  fileType,
  fileDir = null,
  mimePrefix = null,
) {
  const SCRIBENOTE_FLASK = getTempScribenoteFlask();

  let url = `${SCRIBENOTE_FLASK}/sign_s3_attachment/?fileType=${fileType}`;
  //specifiying the subdirectory in the s3 bucket (optional)
  if (fileDir) {
    url += `&fileDir=${encodeURIComponent(fileDir)}`;
  }

  if (mimePrefix) {
    url += `&mimePrefix=${encodeURIComponent(mimePrefix)}`;
  }

  return await postS3(url);
}

export async function signS3Inventory(fileType) {
  const SCRIBENOTE_FLASK = getTempScribenoteFlask();
  const url = `${SCRIBENOTE_FLASK}/sign_s3_inventory/?fileType=${fileType}`;
  return await postS3(url);
}

export async function signS3CompanionSessionAttachment(
  fileType,
  companionSessionUuid,
) {
  const SCRIBENOTE_FLASK = getTempScribenoteFlask();
  const url = `${SCRIBENOTE_FLASK}/sign_s3_companion_session_attachment/?fileType=${fileType}&companionSessionUuid=${companionSessionUuid}`;
  return await postS3(url);
}

export async function signS3AgentSessionAttachment(
  fileType,
  agentSessionUuid,
) {
  const SCRIBENOTE_FLASK = getTempScribenoteFlask();
  const url = `${SCRIBENOTE_FLASK}/sign_s3_agent_session_attachment/?fileType=${encodeURIComponent(fileType)}&agentSessionUuid=${encodeURIComponent(agentSessionUuid)}`;
  return await postS3(url);
}

async function postS3(endpoint) {
  return await axios.get(endpoint, {
    headers: API_HEADERS.headers(),
    withCredentials: true,
  });
}

function createS3Chunks(file) {
  let chunkSize = 5 * 1024 * 1024; // 5 MB is the min size allowed by s3
  let start = 0;
  let end = file.size;
  let chunks = [];
  while (start < end) {
    chunks.push(file.slice(start, start + chunkSize));
    start += chunkSize;
  }

  // map as 1 indexed dict because thats what s3 wants
  return chunks.reduce((res, blob, idx) => {
    res[idx + 1] = blob;
    return res;
  }, {});
}

async function uploadS3MultipartChunks(
  signedUrls,
  chunks,
  setUploadStatus,
) {
  return await Promise.all(
    Object.entries(signedUrls).map(([partNo, signedUrl]) =>
      uploadS3MultipartChunk(
        partNo,
        signedUrl,
        chunks[partNo],
        setUploadStatus,
      ),
    ),
  );
}

async function uploadS3MultipartChunk(
  partNo,
  signedURL,
  chunk,
  setUploadStatus,
) {
  const res = await fetch(signedURL, { method: "PUT", body: chunk });
  setUploadStatus((prev) => ({
    ...prev,
    numChunksCompleted: prev.numChunksCompleted + 1,
  }));
  return {
    ETag: res.headers.get("etag"),
    PartNumber: parseInt(partNo),
  };
}

async function completeS3Multipart(fileName, uploadId, parts) {
  const SCRIBENOTE_FLASK = getTempScribenoteFlask();

  return await axios.post(
    `${SCRIBENOTE_FLASK}/complete_s3_multipart/`,
    { fileName, parts, uploadId },
    {
      headers: API_HEADERS.headers(),
      withCredentials: true,
    },
  );
}

async function abortS3Multipart(fileName, uploadId) {
  const SCRIBENOTE_FLASK = getTempScribenoteFlask();

  return await axios.post(
    `${SCRIBENOTE_FLASK}/abort_s3_multipart/`,
    { fileName, uploadId },
    {
      headers: API_HEADERS.headers(),
      withCredentials: true,
    },
  );
}

async function signS3Multipart(numParts, mimePrefix = "audio/") {
  const SCRIBENOTE_FLASK = getTempScribenoteFlask();

  return await axios.post(
    `${SCRIBENOTE_FLASK}/sign_s3_multipart/`,
    { numParts, mimePrefix },
    {
      headers: API_HEADERS.headers(),
      withCredentials: true,
    },
  );
}

export async function s3MultipartUpload(
  blob,
  uploadStatus,
  setUploadStatus,
  syncedRecordingUUID,
) {
  var fileName, uploadId, signedUrls;
  try {
    const chunks = createS3Chunks(blob);
    const totalChunks = Object.keys(chunks).length;
    setUploadStatus({
      numChunksTotal: totalChunks,
      numChunksCompleted: 0,
    });
    const { data } = await signS3Multipart(totalChunks, "audio/");
    fileName = data.file_name;
    uploadId = data.upload_id;
    signedUrls = data.signed_urls;

    const parts = await uploadS3MultipartChunks(
      signedUrls,
      chunks,
      setUploadStatus,
    );

    const {
      data: { audioStorageLink, storageObjectName },
    } = await completeS3Multipart(fileName, uploadId, parts);
    return { audioStorageLink, storageObjectName };
  } catch (error) {
    mixpanel.track("Error in S3 multipart upload", {
      error: error.message || error,
      fileSizeMB: bytesToMB(blob.size).toFixed(2),
      fileName,
      uploadId,
      syncedRecordingUUID,
      numChunksTotal: uploadStatus?.numChunksTotal,
      numChunksCompleted: uploadStatus?.numChunksCompleted,
    });
    if (fileName && uploadId) {
      abortS3Multipart(fileName, uploadId);
    }
    throw error;
  }
}
